import React, { useState, useEffect } from 'react';
import { X, Upload, Sparkles, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface OnboardingWalkthroughProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const OnboardingWalkthrough: React.FC<OnboardingWalkthroughProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Reset to step 0 when modal opens
    if (isOpen) {
      setStep(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const steps = [
    {
      title: "Welcome to ClipThis! 👋",
      description: "Let's create your first viral clip together. It takes about 2 minutes.",
      icon: <Sparkles className="w-12 h-12 text-red-600" />,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">What you'll learn:</h4>
            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" />
                How to upload a video
              </div>
              <div className="flex items-center text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" />
                How our AI finds the best moments
              </div>
              <div className="flex items-center text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" />
                How to download your clips
              </div>
            </div>
          </div>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">
              <strong>💡 Pro Tip:</strong> For best results, upload a 10-20 minute video with reactions, gameplay, or commentary. The AI works best with videos that have audio spikes and facial expressions.
            </p>
          </div>
        </div>
      ),
      buttonText: "Let's Get Started",
      showSkip: true
    },
    {
      title: "Step 1: Upload Your Video",
      description: "Choose a video file from your computer or paste a YouTube/Twitch URL",
      icon: <Upload className="w-12 h-12 text-red-600" />,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">What videos work best?</h4>
            <div className="space-y-3">
              <div>
                <div className="flex items-center text-sm font-medium text-gray-900 mb-1">
                  ✅ Great for ClipThis:
                </div>
                <ul className="text-sm text-gray-600 space-y-1 ml-4">
                  <li>• Gaming streams with reactions</li>
                  <li>• Reaction videos or commentary</li>
                  <li>• Podcast highlights</li>
                  <li>• Tutorial videos with key moments</li>
                </ul>
              </div>
              
              <div>
                <div className="flex items-center text-sm font-medium text-gray-900 mb-1">
                  ❌ Not ideal:
                </div>
                <ul className="text-sm text-gray-600 space-y-1 ml-4">
                  <li>• Silent videos (no audio)</li>
                  <li>• Static screen recordings</li>
                  <li>• Very short videos (&lt;5 minutes)</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs text-red-800">
              <strong>Free users:</strong> You get 3 videos per month. Make them count!
            </p>
          </div>
        </div>
      ),
      buttonText: "Got It, Let's Upload",
      showSkip: true
    },
    {
      title: "Step 2: AI Analysis",
      description: "Our AI will analyze your video for the best moments",
      icon: <Sparkles className="w-12 h-12 text-red-600" />,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">What happens during analysis?</h4>
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  🎵
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">Audio Spike Detection</div>
                  <div className="text-xs text-gray-600">Finds loud moments, reactions, and excitement</div>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  😊
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">Emotion Detection</div>
                  <div className="text-xs text-gray-600">Analyzes facial expressions for genuine reactions</div>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  ✂️
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">Smart Clipping</div>
                  <div className="text-xs text-gray-600">Creates 15-60 second clips with perfect timing</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-sm text-gray-800">
              <strong>⏱️ Processing time:</strong> Usually 2-5 minutes for a 20-minute video. We'll email you when it's ready!
            </p>
          </div>
        </div>
      ),
      buttonText: "Sounds Good",
      showSkip: true
    },
    {
      title: "Step 3: Review & Download",
      description: "Check your clips and download the ones you love",
      icon: <CheckCircle className="w-12 h-12 text-red-600" />,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">After processing:</h4>
            <div className="space-y-2">
              <div className="flex items-start text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>View all generated clips in your dashboard</span>
              </div>
              <div className="flex items-start text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>Preview each clip before downloading</span>
              </div>
              <div className="flex items-start text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>Download in multiple formats (MP4, WebM)</span>
              </div>
              <div className="flex items-start text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>Share directly to social media</span>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-gray-800 mb-2">
              <strong>🎯 Your Goal:</strong> Upload 2-3 videos to see how consistent our AI is. Each video type works differently!
            </p>
            <p className="text-xs text-gray-600">
              Try different content: gaming, reactions, tutorials. See what works best for you.
            </p>
          </div>
        </div>
      ),
      buttonText: "I'm Ready to Upload",
      showSkip: false
    }
  ];

  const currentStep = steps[step];
  const isLastStep = step === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
      navigate('/upload');
    } else {
      setStep(step + 1);
    }
  };

  const handleSkip = () => {
    onComplete();
    navigate('/upload');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 animate-fade-up">
        {/* Header */}
        <div className="relative p-6 border-b border-gray-200">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              {currentStep.icon}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900">{currentStep.title}</h2>
              <p className="text-gray-600 mt-1">{currentStep.description}</p>
            </div>
          </div>
          
          {/* Progress indicator */}
          <div className="mt-4 flex items-center space-x-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                  index <= step ? 'bg-red-600' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {currentStep.content}
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50 rounded-b-2xl flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Step {step + 1} of {steps.length}
          </div>
          
          <div className="flex items-center space-x-3">
            {currentStep.showSkip && (
              <button
                onClick={handleSkip}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                Skip Tutorial
              </button>
            )}
            
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-all duration-200 flex items-center"
            >
              {currentStep.buttonText}
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWalkthrough;

